import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { useLazyQuery } from '@apollo/client';
import { useManagedMutation } from 'utils/managedHooks';
import { Link } from 'react-router-dom';

import { GET_MEMBERSHIP_DETAILS } from 'store/queries';
import { ADD_NOTE_TO_BOOKING, MAKE_LOYALTY_PAYMENT } from 'store/mutations';
import currencySymbols from 'constants/currencies';
import { codes } from 'constants/payment';
import { paymentsShape } from 'constants/propTypes';
import { getBookingJourneysURL } from 'utils/urls';
import { getMembershipNumberInBooking } from 'pages/PrrRefund/PrrRefund.utils';

import SplitPayment from '../../SplitPayment';
import { Errors } from '../../SplitPayment/SplitPayment.styles';

import { StyledSelect } from './LoyaltyPoints.styles';

const LoyaltyPoints = ({
  totalPriceToBePaid,
  onAmountChange,
  isOnHold,
  reference,
  currency,
  loading,
  payments = [],
  additionalDetails = []
}) => {
  const history = useHistory();
  const [currentDiscount, setCurrentDiscount] = useState('');
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const isMissedDeparture = new URLSearchParams(history.location.search).get('isMissedDeparture') === 'true';
  const isFlowForwardCapped = new URLSearchParams(history.location.search).get('isFlowForwardCapped') === 'true';
  const attachedMembershipNumber = getMembershipNumberInBooking(additionalDetails);
  const isWrongAccountLoggedIn = attachedMembershipNumber && membershipNumber && membershipNumber !== attachedMembershipNumber;

  const hasLoyaltyPayments = !!payments.find((p) => p.method === codes.LOYALTY_POINTS && p.isSuccessful);

  const [makeLoyaltyPayment, { loading: makeLoyaltyPaymentLoading }] = useManagedMutation(MAKE_LOYALTY_PAYMENT);

  const [addNote, { loading: addNoteLoading }] = useManagedMutation(ADD_NOTE_TO_BOOKING, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const [getMembershipInfo, { loading: membershipInfoLoading, data: membershipInfoData, error: membershipError }] =
    useLazyQuery(GET_MEMBERSHIP_DETAILS);

  const membershipInfo = membershipInfoData?.getCustomerDetails;

  useEffect(() => {
    if (!isWrongAccountLoggedIn && membershipNumber) {
      getMembershipInfo({
        variables: {
          membershipNumber,
          bookingReference: reference
        }
      });
    }
  }, [getMembershipInfo, membershipNumber, reference, isWrongAccountLoggedIn]);

  const isLoading = loading || membershipInfoLoading || makeLoyaltyPaymentLoading || addNoteLoading;

  const handleChange = (e) => {
    setCurrentDiscount(e.value);

    if (onAmountChange) {
      onAmountChange(e.value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const points = filteredDiscounts.find(({ amount }) => amount === currentDiscount)?.points ?? 0;

    await makeLoyaltyPayment({
      variables: {
        customerId: membershipInfo?.profile?.customerId,
        isDepositPayment: isOnHold,
        bookingReference: reference,
        cashValue: currentDiscount,
        points,
        currency,
        membershipNumber: membershipInfo?.profile?.loyalty?.membershipNumber,
        sendConfirmationEmail:
          ['booking', 'exchange'].includes(history.location.pathname.split('/')[1]) && !history.location.search.includes('cancel-voucher'),
        isMissedDeparture,
        isFlowForwardCapped
      }
    });

    await addNote({
      variables: {
        note: `Discount using points for Club Eurostar account: ${membershipNumber}, totalPointsSpent: ${points}`,
        bookingReference: reference
      }
    });

    const isTotalPrice = currentDiscount >= totalPriceToBePaid;
    if (isTotalPrice || isOnHold) {
      const bookingJourneysURL = getBookingJourneysURL(reference, membershipNumber);
      history.push(bookingJourneysURL);
    }
  };

  const discountRewards = membershipInfo?.profile?.loyalty?.availableLoyaltyDiscounts || [];

  const filteredDiscounts =
    discountRewards?.filter(
      (discount) =>
        discount.currency === currency &&
        totalPriceToBePaid >= discount.amount &&
        membershipInfo?.profile?.loyalty?.rewardPoints >= discount.points
    ) || [];

  const options = filteredDiscounts.map((discount) => ({
    value: discount.amount,
    label: (
      <FormattedMessage
        id="payments.loyaltyPoints.pointsLabel"
        values={{
          points: discount.points,
          cost: `${currencySymbols[discount.currency]}${discount.amount}`
        }}
      />
    )
  }));

  const hasFetchError = membershipError;
  const hasNotEnoughPointsError = membershipInfo?.profile?.loyalty?.membershipNumber && !isLoading && filteredDiscounts.length === 0;
  const hasNoLoyaltyAccount = !membershipInfo?.profile?.loyalty?.membershipNumber;

  return (
    <SplitPayment
      title={<FormattedMessage id="payments.AmountToPayByLoyaltyPoints" />}
      subTitle={<FormattedMessage id="payments.canBeSplit" />}
      label={<FormattedMessage id="payments.recordPayment" />}
      loading={isLoading}
      disabled={!currentDiscount || hasLoyaltyPayments}
      onSubmit={handleSubmit}
      addBreak={true}
    >
      <StyledSelect
        placeholder={<FormattedMessage id="payments.loyaltyPoints.selectOption" />}
        isDisabled={!filteredDiscounts.length || hasLoyaltyPayments}
        isSearchable={false}
        onChange={handleChange}
        options={options}
        id="discount-select"
      />
      <Errors data-cy="loyalty-payment-errors">
        {isWrongAccountLoggedIn && (
          <FormattedMessage
            id="errorBoundary.mismatchedLoyalty"
            data-test="mismatched-loyalty-account-error"
            values={{
              here: (
                <Link
                  to={{
                    pathname: '/clubeurostar/manage',
                    state: {
                      prevPath: history.location.pathname,
                      prevSearch: history.location.search,
                      prevHash: history.location.hash
                    }
                  }}
                >
                  <FormattedMessage id="errorBoundary.linkHere" />
                </Link>
              )
            }}
          />
        )}

        {hasNoLoyaltyAccount && !isWrongAccountLoggedIn && (
          <FormattedMessage
            id="errorBoundary.notLoggedInLoyalty"
            data-test="no-loyalty-account-error"
            values={{
              here: (
                <Link
                  to={{
                    pathname: '/clubeurostar/manage',
                    state: {
                      prevPath: history.location.pathname,
                      prevSearch: history.location.search,
                      prevHash: history.location.hash
                    }
                  }}
                >
                  <FormattedMessage id="errorBoundary.linkHere" />
                </Link>
              )
            }}
          />
        )}

        {hasFetchError && <FormattedMessage id="payments.tryAgain" data-test="try-again-error" />}
        {hasNotEnoughPointsError && <FormattedMessage id="payments.noEnoughPoints" data-test="no-enough-points-error" />}
      </Errors>
    </SplitPayment>
  );
};

LoyaltyPoints.propTypes = {
  onAmountChange: PropTypes.func,
  totalPriceToBePaid: PropTypes.number,
  loading: PropTypes.bool,
  isOnHold: PropTypes.bool,
  reference: PropTypes.string,
  currency: PropTypes.string,
  payments: paymentsShape,
  additionalDetails: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.string
    })
  )
};

export default LoyaltyPoints;
